import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  CreditCardIcon,
  DollarSignIcon,
  HistoryIcon,
  SettingsIcon
} from "lucide-react";
import BillingOverview from "./BillingOverview";
import BillingPaymentMethods from "./BillingPaymentMethods";
import BillingHistory from "./BillingHistory";
import BillingPreferences from "./BillingPreferences";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import { cn } from "../../lib/utils";

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const BillingBase: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Map hash to tab keys
  const tabKeys: any = {
    overview: "overview",
    paymentMethods: "paymentMethods",
    history: "history",
    preferences: "preferences",
  };

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    setActiveTab(tabKeys[hash] || "overview");
  }, [location]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    navigate(`#${key}`);
  };

  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <DollarSignIcon className="w-4 h-4" />,
      content: <BillingOverview onTabChange={handleTabChange} />
    },
    {
      key: "paymentMethods",
      label: "Payment Methods",
      icon: <CreditCardIcon className="w-4 h-4" />,
      content: <BillingPaymentMethods />
    },
    {
      key: "history",
      label: "Billing History",
      icon: <HistoryIcon className="w-4 h-4" />,
      content: <BillingHistory />
    },
    {
      key: "preferences",
      label: "Preferences",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: <BillingPreferences />
    }
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map(tab => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon
  }));

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Billing</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Billing
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage your payment methods, billing history, and preferences
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <OverflowTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find(tab => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BillingBase;
